import React, { useState, useEffect } from 'react';
import { Card, Steps, Button, Typography, Row, Col, Space, Badge, Tag } from 'antd';
import { 
  CheckCircleOutlined, 
  RocketOutlined, 
  SendOutlined,
  ThunderboltOutlined,
  FireOutlined,
  StarOutlined,
  TrophyOutlined,
  BulbOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useGetSurveysQuery, useCompleteOnboardingMutation } from '../services/nestjs-base-api';
import '../styles/revolutionary-dashboard.css';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface OnboardingWizardProps {
  onComplete: () => void;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [step2Completed, setStep2Completed] = useState(false);
  const [completeOnboarding] = useCompleteOnboardingMutation();

  // Fetch surveys to check if step 1 is completed
  const { data: surveys = [], isLoading: surveysLoading } = useGetSurveysQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  const step1Completed = surveys.length > 0;
  const firstSurvey = surveys[0];

  useEffect(() => {
    // Auto-advance to step 2 if step 1 is completed
    if (step1Completed && currentStep === 0) {
      setCurrentStep(1);
    }
  }, [step1Completed, currentStep]);

  const handleCreateSurvey = () => {
    navigate('/dashboard/surveys/create');
  };

  const handleViewSurvey = () => {
    if (firstSurvey) {
      navigate(`/dashboard/surveys/${firstSurvey.hash}`, {
        state: { scrollToPublicUrl: true }
      });
    }
  };

  const handleStep2Complete = () => {
    setStep2Completed(true);
    
    // Smooth scroll to show the congratulations message
    setTimeout(() => {
      const congratsElement = document.getElementById('congratulations-message');
      if (congratsElement) {
        congratsElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleFinalComplete = async () => {
    // Mark onboarding as completed in the backend
    if (user?.jwtToken) {
      try {
        await completeOnboarding({ jwtToken: user.jwtToken });
        onComplete();
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
      }
    }
  };

  const steps = [
    {
      title: 'Create Your First Survey',
      icon: <RocketOutlined style={{ fontSize: '20px' }} />,
      completed: step1Completed,
      content: (
        <Card 
          className="revolutionary-step-card glassmorphism-card"
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
            border: '2px solid rgba(24, 144, 255, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '350px' }}>
            <Col xs={24} md={12} style={{ 
              background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1), rgba(24, 144, 255, 0.05))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Badge dot style={{ background: '#52c41a' }}>
                  <RocketOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                </Badge>
                <div style={{ marginTop: '16px' }}>
                  <Tag color="blue">AI-Powered</Tag>
                  <Tag color="green">Smart Questions</Tag>
                  <Tag color="purple">Brand Science</Tag>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <FireOutlined style={{ color: '#F59E0B', fontSize: '24px' }} />
                <Title level={3} style={{ 
                  color: '#F59E0B',
                  margin: 0,
                  fontWeight: '700'
                }}>
                  Launch Your Brand Analytics
                </Title>
              </div>
              <Paragraph style={{ fontSize: '16px', marginBottom: '20px', color: '#374151' }}>
                <strong>Revolutionary brand tracking starts here.</strong> Create your first survey powered by scientific methodology 
                and AI-driven insights that measure what truly matters.
              </Paragraph>
              <div style={{ 
                background: 'rgba(24, 144, 255, 0.05)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <BulbOutlined style={{ color: '#faad14' }} />
                  <Text strong>What you'll measure:</Text>
                </div>
                <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6b7280' }}>
                  <li>Brand Awareness & Recognition</li>
                  <li>Customer Loyalty & Advocacy</li>
                  <li>Perceived Quality & Value</li>
                  <li>Emotional Brand Associations</li>
                </ul>
              </div>
              {step1Completed ? (
                <div style={{ 
                  background: 'linear-gradient(135deg, #f6ffed, #f0f9ff)',
                  border: '2px solid #52c41a',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                  <div>
                    <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                      Excellent! Survey Created
                    </Text>
                    <div style={{ color: '#6b7280', fontSize: '14px' }}>
                      Your brand analytics engine is ready
                    </div>
                  </div>
                </div>
              ) : (
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ThunderboltOutlined />}
                  onClick={handleCreateSurvey}
                  loading={surveysLoading}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B, #F59E0B)',
                    border: 'none',
                    height: '48px',
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '100%'
                  }}
                  className="revolutionary-button"
                >
                  Create Revolutionary Survey
                </Button>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      title: 'Activate Data Collection',
      icon: <SendOutlined style={{ fontSize: '20px' }} />,
      completed: step2Completed,
      content: (
        <Card 
          className="revolutionary-step-card glassmorphism-card"
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
            border: '2px solid rgba(24, 144, 255, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '350px' }}>
            <Col xs={24} md={12} style={{ 
              background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1), rgba(24, 144, 255, 0.05))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Badge dot style={{ background: '#F59E0B' }}>
                  <SendOutlined style={{ fontSize: '80px', color: '#52c41a' }} />
                </Badge>
                <div style={{ marginTop: '16px' }}>
                  <Tag color="green">Real-time</Tag>
                  <Tag color="blue">Multi-channel</Tag>
                  <Tag color="purple">Analytics-ready</Tag>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <LineChartOutlined style={{ color: '#F59E0B', fontSize: '24px' }} />
                <Title level={3} style={{ 
                  color: '#F59E0B',
                  margin: 0,
                  fontWeight: '700'
                }}>
                  Activate Intelligence Gathering
                </Title>
              </div>
              <Paragraph style={{ fontSize: '16px', marginBottom: '20px', color: '#374151' }}>
                <strong>Deploy your survey to unlock insights.</strong> Share across channels to gather the data 
                that powers your brand equity analytics engine.
              </Paragraph>
              <div style={{ 
                background: 'rgba(24, 144, 255, 0.05)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <TrophyOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Distribution channels:</Text>
                </div>
                <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6b7280' }}>
                  <li>Email campaigns & newsletters</li>
                  <li>Social media platforms</li>
                  <li>Website embeds & pop-ups</li>
                  <li>QR codes & direct links</li>
                </ul>
              </div>
              {step2Completed ? (
                <div style={{ 
                  background: 'linear-gradient(135deg, #f6ffed, #f0f9ff)',
                  border: '2px solid #52c41a',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                  <div>
                    <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                      Perfect! Data Collection Active
                    </Text>
                    <div style={{ color: '#6b7280', fontSize: '14px' }}>
                      Insights will flow in real-time
                    </div>
                  </div>
                </div>
              ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<SendOutlined />}
                    onClick={handleViewSurvey}
                    disabled={!firstSurvey}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B, #F59E0B)',
                      border: 'none',
                      height: '48px',
                      fontWeight: '600',
                      fontSize: '16px',
                      width: '100%'
                    }}
                    className="revolutionary-button"
                  >
                    Get Survey Distribution Link
                  </Button>
                  <Button 
                    type="default" 
                    size="large"
                    onClick={handleStep2Complete}
                    style={{
                      height: '48px',
                      fontWeight: '600',
                      fontSize: '16px',
                      width: '100%',
                      border: '2px solid #F59E0B'
                    }}
                  >
                    I've Activated Distribution
                  </Button>
                </Space>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
  ];

  return (
    <div className="revolutionary-dashboard gpu-accelerated" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Compact Revolutionary Hero Section */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #F59E0B 0%, #F59E0B 50%, #FBBF24 100%)',
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden'
      }}
      className="revolutionary-hero glassmorphism-card"
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
            <FireOutlined style={{ fontSize: '32px', color: '#fff' }} />
            <Title level={2} style={{ 
              color: 'white',
              margin: 0,
              fontSize: '32px',
              fontWeight: '800',
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              Welcome to Your Brand Revolution
            </Title>
          </div>
          
          <Paragraph style={{ 
            fontSize: '16px', 
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '700px',
            margin: '0 auto 16px',
            fontWeight: '500'
          }}>
            Set up your AI-powered brand equity command center in just 2 simple steps
          </Paragraph>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <Tag color="#52c41a" style={{ fontSize: '12px', fontWeight: '600' }}>
              AI-Powered
            </Tag>
            <Tag color="#F59E0B" style={{ fontSize: '12px', fontWeight: '600' }}>
              Scientific Method
            </Tag>
            <Tag color="#fa8c16" style={{ fontSize: '12px', fontWeight: '600' }}>
              Real-time Insights
            </Tag>
          </div>
        </div>
      </div>

      {/* Progress Steps with Enhanced Styling */}
      <Card
        className="glassmorphism-card"
        style={{
          marginBottom: '32px',
          background: 'rgba(255,255,255,0.9)',
          border: '1px solid rgba(24, 144, 255, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Steps 
          current={currentStep} 
          size="default"
          style={{ padding: '16px 0' }}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={
                <span style={{ 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: step.completed ? '#52c41a' : index === currentStep ? '#F59E0B' : '#8c8c8c'
                }}>
                  {step.title}
                </span>
              }
              icon={
                step.completed ? (
                  <div style={{
                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                ) : (
                  <div style={{
                    background: index === currentStep 
                      ? 'linear-gradient(135deg, #F59E0B, #F59E0B)'
                      : '#f0f0f0',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(step.icon, { 
                      style: { 
                        ...step.icon.props.style,
                        color: index === currentStep ? 'white' : '#8c8c8c' 
                      }
                    })}
                  </div>
                )
              }
              status={step.completed ? 'finish' : index === currentStep ? 'process' : 'wait'}
            />
          ))}
        </Steps>
      </Card>

      {/* Step Content */}
      <div key={currentStep} style={{ minHeight: '400px' }}>
        {steps[currentStep]?.content}
      </div>

      {/* Revolutionary Congratulations Section */}
      {step1Completed && step2Completed && (
        <Card 
          id="congratulations-message"
          className="glassmorphism-card"
          style={{ 
            marginTop: '48px',
            background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 50%, #fff7e6 100%)',
            border: '3px solid transparent',
            borderImage: 'linear-gradient(135deg, #52c41a, #F59E0B, #fa8c16) 1',
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ 
              fontSize: '80px', 
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              borderRadius: '50%',
              width: '120px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <TrophyOutlined style={{ color: 'white' }} />
            </div>
            
            <Title level={2} style={{ 
              background: 'linear-gradient(45deg, #F59E0B, #D97706, #B45309)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '16px',
              fontSize: '36px',
              fontWeight: '800'
            }}>
              BrandScience.ai Setup Complete!
            </Title>
            
            <Paragraph style={{ fontSize: '20px', marginBottom: '24px', color: '#374151', fontWeight: '500' }}>
              Your brand equity command centre is now operational
            </Paragraph>
            
            <div style={{ 
              maxWidth: '600px',
              margin: '0 auto 32px',
              padding: '24px',
              background: 'rgba(255,255,255,0.8)',
              borderRadius: '16px',
              border: '1px solid rgba(24, 144, 255, 0.2)'
            }}>
              <Row gutter={[24, 16]}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <StarOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#F59E0B' }}>100%</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Setup Complete</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <RocketOutlined style={{ fontSize: '32px', color: '#F59E0B', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#F59E0B' }}>Live</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Data Collection</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <ThunderboltOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#F59E0B' }}>Ready</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>For Analytics</div>
                  </div>
                </Col>
              </Row>
            </div>
            
            <Paragraph style={{ fontSize: '18px', marginBottom: '32px', color: '#6b7280' }}>
              As responses flow in, you'll unlock powerful AI-driven insights about brand strength, 
              customer perception, and growth opportunities. Your journey to brand excellence starts now!
            </Paragraph>
            
            <Button 
              type="primary" 
              size="large"
              icon={<LineChartOutlined />}
              onClick={() => {
                handleFinalComplete();
              }}
              style={{
                background: 'linear-gradient(135deg, #F59E0B, #F59E0B, #FBBF24)',
                border: 'none',
                height: '56px',
                padding: '0 48px',
                fontSize: '18px',
                fontWeight: '700',
                borderRadius: '28px',
                boxShadow: '0 8px 24px rgba(24, 33, 109, 0.3)'
              }}
              className="revolutionary-button"
            >
              Enter Your Brand Equity Command Centre
            </Button>
          </div>
        </Card>
      )}

      <style>{`
        .revolutionary-step-card {
          border: none !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
          transition: all 0.3s ease !important;
        }
        .revolutionary-step-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
        }
        .revolutionary-step-card .ant-card-body {
          padding: 0 !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-wait .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};